import { useRef, useState } from "react";
import { MAX_TITLE_LENGTH } from "../../../types";

type Props = {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  // Min-width utility for the wrapper (default min-w-0); lets a flex-wrap form wrap instead of shrinking.
  minWidthClass?: string;
  placeholder?: string;
  autoFocus?: boolean;
};

// Every todo/step title input goes through here so the shared limit is applied
// once. maxLength truncates pasted text silently, so onPaste records what was
// pasted and onChange compares the real result with what an uncut paste would have
// produced; we only claim truncation when text was cut, otherwise the hint stays
// neutral ("Limit reached"). The record is dropped on keydown, blur, or right
// after the paste event. Browsers differ on newlines in a single-line input
// (Chrome strips them, Firefox turns them into spaces), so both outcomes count
// as uncut. A paste into an already-full input with nothing selected fires no input
// event, so onPaste flags that case as truncated itself.
export default function TitleInput({ value, onChange, className, minWidthClass, placeholder, autoFocus }: Props) {
  const [truncated, setTruncated] = useState(false);
  const pending = useRef<{ before: string; after: string; pasted: string } | null>(null);
  return (
    <div className={`flex-1 ${minWidthClass ?? "min-w-0"}`}>
      <input
        className={`w-full ${className ?? ""}`}
        placeholder={placeholder}
        maxLength={MAX_TITLE_LENGTH}
        value={value}
        onChange={(e) => {
          const next = e.target.value;
          const p = pending.current;
          pending.current = null;
          if (p) {
            const uncut = [p.pasted.replace(/[\r\n]+/g, ""), p.pasted.replace(/[\r\n]/g, " ")];
            setTruncated(!uncut.some((c) => next === p.before + c + p.after));
          } else if (next.length < MAX_TITLE_LENGTH) setTruncated(false);
          onChange(e.target.value);
        }}
        onKeyDown={() => {
          pending.current = null;
          setTruncated(false);
        }}
        onBlur={() => {
          pending.current = null;
        }}
        onPaste={(e) => {
          const el = e.currentTarget;
          const start = el.selectionStart ?? value.length;
          const end = el.selectionEnd ?? value.length;
          pending.current = { before: value.slice(0, start), after: value.slice(end), pasted: e.clipboardData.getData("text") };
          // The input event for a paste fires in the same task; if none does (cancelled, or already
          // at maxLength) drop the record so it can't make a later keystroke look like a cut paste.
          const mine = pending.current;
          // Already full with nothing selected: no input event will follow, but the whole paste was dropped.
          if (value.length >= MAX_TITLE_LENGTH && start === end && mine.pasted.replace(/[\r\n]/g, "") !== "") setTruncated(true);
          setTimeout(() => {
            if (pending.current === mine) pending.current = null;
          }, 0);
        }}
        autoFocus={autoFocus}
      />
      {value.length >= MAX_TITLE_LENGTH && (
        <p role="status" className="text-xs text-amber-600 mt-1">
          {truncated
            ? `Pasted text was truncated to ${MAX_TITLE_LENGTH} characters`
            : `Limit reached (${MAX_TITLE_LENGTH} characters)`}
        </p>
      )}
    </div>
  );
}
